import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Search, TrendingUp, Globe, FileText, Image, Zap, RefreshCw, CheckCircle, AlertCircle, Loader2, ExternalLink, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminSEM = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatingSingle, setGeneratingSingle] = useState(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API}/api/seo/dashboard`, { headers: getAuthHeader() });
      const data = await res.json();
      setDashboard(data);
    } catch (err) {
      toast.error('Erreur de chargement SEO');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboard(); }, []);

  const handleGenerateAll = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/seo/generate-all`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      const data = await res.json();
      toast.success(data.message || 'Génération SEO lancée');
      setTimeout(() => fetchDashboard(), 5000);
    } catch (err) {
      toast.error('Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateSingle = async (vehicleId) => {
    setGeneratingSingle(vehicleId);
    try {
      const res = await fetch(`${API}/api/seo/generate/${vehicleId}`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      const data = await res.json();
      if (data.status === 'success') {
        toast.success('SEO généré avec succès');
        fetchDashboard();
      } else {
        toast.error(data.error || 'Erreur');
      }
    } catch (err) {
      toast.error('Erreur lors de la génération');
    } finally {
      setGeneratingSingle(null);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 50) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'from-green-500/10 to-green-600/5 border-green-200';
    if (score >= 50) return 'from-yellow-500/10 to-yellow-600/5 border-yellow-200';
    return 'from-red-500/10 to-red-600/5 border-red-200';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-sem">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">SEM / SEO Automatisé</h1>
            <p className="text-slate-500 text-sm mt-1">Optimisation moteurs de recherche propulsée par IA</p>
          </div>
          <button
            onClick={handleGenerateAll}
            disabled={generating}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors"
            data-testid="generate-all-seo-btn"
          >
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
            {generating ? 'Génération en cours...' : 'Optimiser tout'}
          </button>
        </div>

        {/* Score + Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {/* SEO Score */}
          <div className={`bg-gradient-to-br ${getScoreBg(dashboard?.seo_score || 0)} border rounded-xl p-4 col-span-1`}>
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Score SEO</p>
            <p className={`text-4xl font-black ${getScoreColor(dashboard?.seo_score || 0)}`}>
              {dashboard?.seo_score || 0}%
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <Globe className="h-4 w-4 text-blue-500" />
              <p className="text-xs text-slate-500 uppercase tracking-wider">Pages indexées</p>
            </div>
            <p className="text-2xl font-bold text-slate-900">{dashboard?.indexed_pages || 0}</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <Search className="h-4 w-4 text-green-500" />
              <p className="text-xs text-slate-500 uppercase tracking-wider">Mots-clés SEO</p>
            </div>
            <p className="text-2xl font-bold text-slate-900">
              {dashboard?.with_seo_keywords || 0}
              <span className="text-sm text-slate-400 font-normal">/{dashboard?.total_vehicles || 0}</span>
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="h-4 w-4 text-purple-500" />
              <p className="text-xs text-slate-500 uppercase tracking-wider">Descriptions</p>
            </div>
            <p className="text-2xl font-bold text-slate-900">
              {dashboard?.with_description || 0}
              <span className="text-sm text-slate-400 font-normal">/{dashboard?.total_vehicles || 0}</span>
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <Image className="h-4 w-4 text-orange-500" />
              <p className="text-xs text-slate-500 uppercase tracking-wider">Vraies photos</p>
            </div>
            <p className="text-2xl font-bold text-slate-900">
              {dashboard?.with_real_images || 0}
              <span className="text-sm text-slate-400 font-normal">/{dashboard?.total_vehicles || 0}</span>
            </p>
          </div>
        </div>

        {/* SEO Checklist */}
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            Checklist SEO
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { label: 'Sitemap XML dynamique', ok: true, link: '/api/sitemap.xml' },
              { label: 'Sitemap Images', ok: true, link: '/api/sitemap-images.xml' },
              { label: 'Robots.txt configuré', ok: true, link: null },
              { label: 'Schema.org (JSON-LD) sur toutes les pages', ok: true, link: null },
              { label: 'Balises Open Graph (Facebook/WhatsApp)', ok: true, link: null },
              { label: 'Balises Twitter Card', ok: true, link: null },
              { label: 'URLs canoniques', ok: true, link: null },
              { label: 'Balises hreflang (FR/EN)', ok: true, link: null },
              { label: 'Meta descriptions par véhicule', ok: (dashboard?.with_seo_keywords || 0) > 0, link: null },
              { label: 'Mots-clés IA par véhicule', ok: (dashboard?.with_seo_keywords || 0) > 0, link: null },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                {item.ok ? (
                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-yellow-500 flex-shrink-0" />
                )}
                <span className="text-sm text-slate-700">{item.label}</span>
                {item.link && (
                  <a href={`${API}${item.link}`} target="_blank" rel="noopener noreferrer" className="ml-auto">
                    <ExternalLink className="h-3.5 w-3.5 text-slate-400 hover:text-blue-500" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Vehicles needing optimization */}
        {dashboard?.vehicles_needing_seo?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-slate-900 flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-yellow-500" />
                Véhicules à optimiser ({dashboard.needs_optimization})
              </h2>
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {dashboard.vehicles_needing_seo.map((v) => (
                <div key={v.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                  <div>
                    <span className="font-medium text-slate-800 text-sm">
                      {v.year} {v.make} {v.model} {v.trim || ''}
                    </span>
                    {v.stock_number && (
                      <span className="text-xs text-slate-400 ml-2">#{v.stock_number}</span>
                    )}
                  </div>
                  <button
                    onClick={() => handleGenerateSingle(v.id)}
                    disabled={generatingSingle === v.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-xs font-medium disabled:opacity-50 transition-colors"
                    data-testid={`generate-seo-${v.id}`}
                  >
                    {generatingSingle === v.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Zap className="h-3 w-3" />
                    )}
                    {generatingSingle === v.id ? 'Génération...' : 'Optimiser'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Activity */}
        {dashboard?.recent_activity?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-500" />
              Activité récente
            </h2>
            <div className="space-y-2">
              {dashboard.recent_activity.map((log, i) => (
                <div key={i} className="flex items-center justify-between p-2 text-sm border-b border-slate-50 last:border-0">
                  <div className="flex items-center gap-2">
                    <ChevronRight className="h-3 w-3 text-green-500" />
                    <span className="text-slate-700">{log.vehicle_name}</span>
                    <span className="text-xs text-slate-400">{log.keywords_count} mots-clés</span>
                  </div>
                  <span className="text-xs text-slate-400">
                    {log.created_at ? new Date(log.created_at).toLocaleDateString('fr-CA') : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SEO Tips */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5">
          <h2 className="font-bold text-blue-900 mb-3">Recommandations IA</h2>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <span>Cliquez sur <strong>"Optimiser tout"</strong> pour générer automatiquement les mots-clés et meta descriptions pour tous vos véhicules</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <span>Les véhicules avec <strong>vraies photos</strong> se classent mieux sur Google Images - ajoutez des photos dès que possible</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <span>Le sitemap XML est mis à jour automatiquement avec chaque nouveau véhicule ajouté</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <span>Les mots-clés ciblent automatiquement <strong>Montréal, Verdun, Rive-Sud</strong> et les termes bilingues FR/EN</span>
            </li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSEM;
